/* eslint-disable import/no-extraneous-dependencies -- for adobe spectrum, aria, stately. */
import { useLocale } from '@react-aria/i18n';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { AccessForbidden } from './AccessForbidden';
import { NotFound } from './NotFound';
import { ProtectedRoute, ProtectedRouteProps } from './ProtectedRoute';
import {
  PATH_ACCESS_FORBIDDEN,
  PATH_EDIT_CONFIGURATION,
  PATH_MAIN_ROUTE,
  PATH_MY_CONFIGURATIONS,
  PATH_NEW_CONFIGURATION,
} from './routes';
import { useConfig } from '../app/ConfigurationProvider';
import { IntegrationsProvider } from '../app/IntegrationsProvider';
import { Layout } from '../components/layout/Layout';
import { IntegrationSideBarFormWrapper } from '../scene/integrations/IntegrationSideBarFormWrapper';
import { IntegrationsPickerSideBar } from '../scene/integrations/IntegrationsPickerSideBar';
import { MyIntegrationsContainer } from '../scene/integrations/MyIntegrationsContainer';
import { ETL_PERMISSIONS } from '../utils/keycloakUtils';

const protectedInternalRedirect = (props: ProtectedRouteProps, component: JSX.Element) => {
  return <ProtectedRoute {...props}>{component}</ProtectedRoute>;
};

export const Router = () => {
  const { basePath } = useConfig();
  const { direction, locale } = useLocale();

  const htmlAttributes = useMemo(
    () => ({
      lang: locale,
      dir: direction,
    }),
    [direction, locale]
  );

  return (
    <>
      <Helmet htmlAttributes={htmlAttributes} />
      <BrowserRouter basename={basePath}>
        <Routes>
          <Route path={PATH_MAIN_ROUTE} element={<Layout />}>
            <Route
              path={PATH_MY_CONFIGURATIONS}
              element={protectedInternalRedirect(
                {
                  requireAllPermissions: false,
                  permissions: [
                    ETL_PERMISSIONS.getConfiguration,
                    ETL_PERMISSIONS.createConfiguration,
                    ETL_PERMISSIONS.updateConfiguration,
                  ],
                },
                <MyIntegrationsContainer />
              )}
            >
              <Route
                path={PATH_NEW_CONFIGURATION}
                element={protectedInternalRedirect(
                  { permissions: [ETL_PERMISSIONS.createConfiguration] },
                  <IntegrationsPickerSideBar />
                )}
              />
              <Route
                path={`${PATH_NEW_CONFIGURATION}/:integrationType`}
                element={protectedInternalRedirect(
                  { permissions: [ETL_PERMISSIONS.createConfiguration] },
                  <IntegrationsProvider>
                    <IntegrationSideBarFormWrapper />
                  </IntegrationsProvider>
                )}
              />
              <Route
                path={`${PATH_EDIT_CONFIGURATION}/:integrationType/:integrationId`}
                // if edit panel is opened with only get `GetConfiguration` permission, render it as read only
                element={protectedInternalRedirect(
                  { permissions: [ETL_PERMISSIONS.getConfiguration] },
                  <IntegrationsProvider>
                    <IntegrationSideBarFormWrapper />
                  </IntegrationsProvider>
                )}
              />
            </Route>

            {/* others */}
            <Route path={PATH_ACCESS_FORBIDDEN} element={<AccessForbidden />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
};
